import React, {useState, MouseEvent, FC} from 'react';
import Box from '@material-ui/core/Box';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Typography from '@material-ui/core/Typography';
import Popover from '@material-ui/core/Popover';
import Radio from '@material-ui/core/Radio';
import Divider from '@material-ui/core/Divider';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import {ListProps, ListItemProps, PopoverProps, withStyles} from '@material-ui/core';

// @ts-ignore
import {Theme} from '@epic-ui/epic';
import PopperInput from '../common/PopperInput';

import {Option} from '../../types';

const StyledList = withStyles<string, undefined, ListProps & {component: string}>(theme => ({
    root: {
        width: '100%',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    }
}))(List);

export const StyledPopover = withStyles((theme: Theme) => ({
    paper: {
        marginTop: theme.spacing(1),
        backgroundColor: theme.palette.background.overlays,

        width: `calc(100% - ${theme.spacing(8)}px)`,

        [theme.breakpoints.up('fsm')]: {
            width: `calc(100% - ${theme.spacing(24)}px)`
        },
        [theme.breakpoints.up('fmd')]: {
            minWidth: 140,
            width: 'auto'
        }
    }
}))(Popover);

const StyledListItem = withStyles<string, undefined, ListItemProps>(theme => ({
    root: {
        color: theme.palette.text.secondary,
        height: theme.spacing(8),

        '&:active': {
            color: theme.palette.text.primary
        },
        '&.Mui-selected': {
            color: theme.palette.text.primary
        }
    }
}))(ListItem);

export interface RadioOptionSelectProps extends Omit<PopoverProps, 'onChange' | 'open'> {
    options: Option[];
    value: Option;
    underlinedOption?: number;
    onChange: (value: Option) => void;
    onClose?: () => void;
}

const RadioOptionSelect: FC<RadioOptionSelectProps> = ({
    onChange,
    options,
    underlinedOption,
    value: activeOption,
    ...props
}: RadioOptionSelectProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const handleOptionClick = (option: Option) => {
        setAnchorEl(null);
        onChange(option);
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <PopperInput open={open} value={activeOption.label} onClick={handleClick} />
            <StyledPopover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClick}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}
                {...props}>
                <Box mt={2}>
                    <StyledList component="div">
                        {options.map((option, i) => (
                            <React.Fragment key={option.value}>
                                <StyledListItem
                                    button
                                    key={option.label}
                                    selected={activeOption.value === option.value}
                                    onClick={() => handleOptionClick(option)}>
                                    <ListItemIcon>
                                        <Radio
                                            value={option.value}
                                            checked={option.value === activeOption.value}
                                        />
                                    </ListItemIcon>
                                    <Box mr={2}>
                                        <Typography variant="body1">{option.label}</Typography>
                                    </Box>
                                </StyledListItem>
                                {underlinedOption === i && (
                                    <Box mx={3} my={1}>
                                        <Divider />
                                    </Box>
                                )}
                            </React.Fragment>
                        ))}
                    </StyledList>
                </Box>
            </StyledPopover>
        </>
    );
};

export default RadioOptionSelect;
